import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { createApiClient } from "@/api/client";
import { queryKeys, LIVE_REFETCH_MS } from "@/api/queryClient";
import type { AvailableUser, User } from "@/types";

export function useUsers(): UseQueryResult<User[]> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: queryKeys.list("users"),
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<User[]>("/users");
    },
    enabled: !!session,
    // #redesign Welle C (#10, Review M1): konsistent live wie Agents/Channels/Nodes
    // (Users-Liste + Dashboard-KPI + Owner-Auflösung in der Agents-Liste).
    refetchInterval: LIVE_REFETCH_MS,
  });
}

/**
 * #332 (UC-2, Wire aus #331 UC-4): das Firmen-Adressbuch für die Entscheider-Wahl —
 * `GET /users/available?instanceId=&roleId=`.
 *
 * ⚠ Bewusst NICHT `useUsers()`: `GET /users` ist admin-only (`users:read`) und liefert die
 * VERWALTUNGS-Sicht inkl. External Users und deaktivierter Konten. Dieser Read hängt an der
 * eigenen Capability **`users:directory:read`** (Vorgang `user-directory.browse`, Sicht-Gate
 * Inhaber der Einrichtung ∪ Admin) und liefert nur interne, aktive Menschen — der Master ist
 * der Wächter, die Oberfläche filtert NICHTS nach.
 *
 * `instanceId` ist Pflicht (Sicht-Gate). `roleId` markiert die Rollen-Mitglieder; ohne sie
 * ist `mitgliedDerRolle` überall `false` — dann gibt es keine Rolle zu markieren.
 */
export function useAvailableUsers(
  options: { instanceId: string; roleId?: string; enabled?: boolean },
): UseQueryResult<AvailableUser[]> {
  const { session, logout } = useAuth();
  const { instanceId, roleId } = options;
  return useQuery({
    // Die Rolle gehört in den Schlüssel: dieselbe Einrichtung markiert je Schritt eine
    // ANDERE Rolle, und ein geteilter Cache zeigte die Markierung des zuletzt geöffneten.
    queryKey: [...queryKeys.list("users"), "available", instanceId, roleId ?? "_ohne-rolle"],
    queryFn: async () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      const q = new URLSearchParams({ instanceId });
      if (roleId) q.set("roleId", roleId);
      const r = await client.get<{ users: AvailableUser[] }>(`/users/available?${q.toString()}`);
      // Skew-fest: eine Antwort ohne `users` ist keine leere Firma — sie ist unbrauchbar.
      // Der Fehler löst in der Karte den Rückfall auf die Rollen-Mitglieder aus.
      if (!Array.isArray(r?.users)) throw new Error("users fehlen");
      return r.users;
    },
    enabled: !!session && !!instanceId && options.enabled !== false,
    // Ein älterer Master kennt die Route nicht (404) — dann soll der Rückfall SOFORT
    // greifen, statt drei Anläufe abzuwarten.
    retry: false,
  });
}

export function useUser(id: string | undefined): UseQueryResult<User> {
  const { session, logout } = useAuth();
  return useQuery({
    queryKey: id ? queryKeys.detail("users", id) : ["resources", "users", "_invalid"],
    queryFn: () => {
      const client = createApiClient({
        getToken: () => session?.accessToken ?? null,
        onUnauthorized: logout,
      });
      return client.get<User>(`/users/${id}`);
    },
    enabled: !!id && !!session,
  });
}
